import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-au';

import { Panel } from '../../../../../components/atoms/Panel';
import { FieldGrid, FieldItem } from '../../../../../components/atoms/FieldGrid';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';
import { PriorityDot } from '../../../../../components/atoms/PriorityDot';
import { JobDetailsPanelProps } from './JobDetailsPanel.props';

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } catch {
    return '—';
  }
};

/** Editable date field — click to open a popover with a date input. */
const EditableDate: React.FC<{
  label: string;
  value?: string;
  type?: 'date' | 'datetime-local';
  displayValue: string;
  onSave?: (isoValue: string) => void;
}> = ({ label, value, type = 'date', displayValue, onSave }) => {
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<dayjs.Dayjs | null>(null);
  const [draftTime, setDraftTime] = useState<dayjs.Dayjs | null>(null);
  const [validationError, setValidationError] = useState('');

  const handleOpen = () => {
    const d = value ? dayjs(value) : null;
    setDraftDate(d?.isValid() ? d : null);
    setDraftTime(d?.isValid() ? d : dayjs().hour(9).minute(0));
    setValidationError('');
    setOpen(true);
  };

  const handleSave = () => {
    if (!onSave) return;
    if (!draftDate || !draftDate.isValid()) { setValidationError('Please enter a valid date'); return; }

    let result = draftDate;
    if (type === 'datetime-local') {
      if (!draftTime || !draftTime.isValid()) { setValidationError('Please enter a valid time'); return; }
      result = draftDate.hour(draftTime.hour()).minute(draftTime.minute()).second(0);
    }

    setValidationError('');
    setOpen(false);
    onSave(result.toISOString());
  };

  return (
    <Box>
      {label && (
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2, mb: 0.25 }}>
          {label}
        </Typography>
      )}
      <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
          {displayValue}
        </Box>
        {onSave && (
          <IconButton size="small" onClick={handleOpen} aria-label={`Edit ${label || 'date'}`}
            sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}>
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1500 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper elevation={8} sx={{ p: 2, mt: 0.5, border: '1px solid', borderColor: 'v4.border', borderRadius: 2, width: 280 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DatePicker
                  label="Date"
                  value={draftDate}
                  onChange={(val) => { setDraftDate(val); setValidationError(''); }}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', autoFocus: true, sx: { '& .MuiOutlinedInput-root': { fontSize: '12px' } } } }}
                />
                {type === 'datetime-local' && (
                  <TimePicker
                    label="Time"
                    value={draftTime}
                    onChange={(val) => { setDraftTime(val); setValidationError(''); }}
                    ampm={true}
                    slotProps={{ textField: { size: 'small', sx: { '& .MuiOutlinedInput-root': { fontSize: '12px' } } } }}
                  />
                )}
                {validationError && (
                  <Typography sx={{ fontSize: '11px', color: 'error.main' }}>{validationError}</Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Box component="button" onClick={() => setOpen(false)}
                    sx={{ fontSize: '11px', px: 1.5, py: 0.5, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', fontFamily: 'inherit' }}>
                    Cancel
                  </Box>
                  <Box component="button" onClick={handleSave}
                    sx={{ fontSize: '11px', fontWeight: 600, px: 1.5, py: 0.5, border: 'none', borderRadius: '4px', bgcolor: 'v4.accent', color: 'common.white', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { opacity: 0.9 } }}>
                    Save
                  </Box>
                </Box>
              </Box>
            </LocalizationProvider>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};


export const JobDetailsPanel: React.FC<JobDetailsPanelProps> = ({
  service,
  onFieldUpdate,
}) => {
  const statusName = service?.serviceStatus?.name ?? '';
  const priorityLabel = service?.priorityLabel ?? service?.priorityCode ?? '';
  const serviceTypeName =
    service?.serviceType?.[0]?.name ?? service?.serviceTypeCode ?? '—';

  return (
    <Panel title="Job Details">
      <FieldGrid columns={2}>
        <FieldItem label="Job Type" value={serviceTypeName} />
        <FieldItem
          label="Status"
          value={statusName ? <StatusBadge status={statusName} /> : '—'}
        />
        <FieldItem
          label="Priority"
          value={priorityLabel ? <PriorityDot priority={priorityLabel} /> : '—'}
        />
        <FieldItem label="Client" value={service?.client ?? '—'} />
        <FieldItem label="HD Operator" value={service?.loggedBy ?? '—'} />
        <FieldItem
          label="Scheduled Date"
          value={
            <EditableDate
              label=""
              value={service?.scheduledAt}
              displayValue={formatDate(service?.scheduledAt)}
              onSave={onFieldUpdate ? (val) => onFieldUpdate('scheduledAt', val) : undefined}
            />
          }
        />
        <FieldItem
          label="Created Date"
          value={formatDate(service?.createdAt)}
        />
        <FieldItem
          label="Follow Up"
          value={
            <EditableDate
              label=""
              value={service?.followUpAt}
              type="datetime-local"
              displayValue={formatDateTime(service?.followUpAt)}
              onSave={onFieldUpdate ? (val) => onFieldUpdate('followUpAt', val) : undefined}
            />
          }
        />
        <FieldItem label="Zone" value={service?.zone ?? '—'} />
      </FieldGrid>
    </Panel>
  );
};
